import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useBooking, RoomSelection } from "../context/BookingContext";
import { useContent } from "../context/ContentProvider";
import { User, Plus, Minus, ArrowRight, Loader2 } from "lucide-react";
import { useRealtimeRooms } from "../hooks/useRealtimeRooms";
import { getFullImageUrl } from "../utils/imageUtils";

const RoomsStep: React.FC = () => {
  const navigate = useNavigate();
  const { formData, selectedRooms, updateSelectedRooms, setCurrentStep } = useBooking();
  const { content } = useContent();
  const [roomCounts, setRoomCounts] = useState<{ [key: string]: number }>({});

  // Real-time rooms hook - uses selectedHotel from context
  const { rooms, loading } = useRealtimeRooms();

  // Debug: Log rooms when they change
  useEffect(() => {
    if (rooms && rooms.length > 0) {
      console.log("🏠 Rooms loaded from real-time hook:", rooms);
      rooms.forEach((room) => {
        console.log(
          `  - Room "${room.name}" (ID: ${room.id}, Price: €${room.base_rate})`
        );
        if (!room.id) {
          console.error("❌ WARNING: Room has no ID:", room);
        }
      });
    }
  }, [rooms]);

  // Redirect if no dates selected
  useEffect(() => {
    if (!formData.checkIn || !formData.checkOut) {
      navigate("/");
      return;
    }

    // Initialize room counts from selected rooms
    const initialCounts: { [key: string]: number } = {};
    selectedRooms.forEach((room) => {
      console.log("Initializing count for room:", room);
      initialCounts[room.id] = room.count;
    });
    setRoomCounts(initialCounts);
  }, [formData, navigate, selectedRooms]);

  const handleRoomCount = (roomId: string, increment: boolean) => {
    console.log("Updating room count:", { roomId, increment });
    const currentCount = roomCounts[roomId] || 0;
    let newCount = increment ? currentCount + 1 : currentCount - 1;

    // Enforce limits
    if (newCount < 0) newCount = 0;
    if (newCount > 5) newCount = 5;

    console.log("New room count:", { roomId, count: newCount });
    setRoomCounts({
      ...roomCounts,
      [roomId]: newCount,
    });
  };

  const getTotalRooms = () => {
    return Object.values(roomCounts).reduce((sum, count) => sum + count, 0);
  };

  const handleContinue = () => {
    // Update selected rooms in context
    const selected: RoomSelection[] = rooms
      .filter((room) => (roomCounts[room.id] || 0) > 0)
      .map((room) => {
        // Validate room ID exists
        if (!room.id) {
          console.error("❌ Room ID is missing for room:", room);
          throw new Error(`Room ID is missing for room: ${room.name}`);
        }

        console.log("✅ Selected room:", {
          id: room.id,
          type: room.name,
          count: roomCounts[room.id],
          price: room.base_rate,
        });

        return {
          id: room.id, // This is the critical field for booking creation
          type: room.name,
          count: roomCounts[room.id] || 0,
          price: room.base_rate,
        };
      });

    // Validate at least one room is selected with valid ID
    if (selected.length === 0) {
      console.error("❌ No rooms selected");
      return;
    }

    const invalidRooms = selected.filter((room) => !room.id);
    if (invalidRooms.length > 0) {
      console.error("❌ Rooms with missing IDs:", invalidRooms);
      alert(
        "Erreur: ID de chambre manquant. Veuillez sélectionner à nouveau vos chambres."
      );
      return;
    }

    console.log("✅ All selected rooms validated:", selected);
    updateSelectedRooms(selected);
    // Navigate to extras step internally
    setCurrentStep(3);
  };

  // Calculate number of nights
  const nights =
    formData.checkIn && formData.checkOut
      ? Math.ceil(
          (formData.checkOut.getTime() - formData.checkIn.getTime()) /
            (1000 * 60 * 60 * 24)
        )
      : 0;

  // Calculate total price
  const totalPrice = Object.keys(roomCounts).reduce((total, roomId) => {
    const room = rooms.find((r) => r.id === roomId);
    return total + (room?.base_rate || 0) * (roomCounts[roomId] || 0) * nights;
  }, 0);

  // Format date for display
  const formatDate = (date: Date | undefined) => {
    if (!date) return "";
    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] py-12 px-4">
        <Loader2 className="w-12 h-12 hotel-loading-spinner animate-spin mb-4" />
        <h3 className="text-xl font-medium hotel-loading">
          Recherche des chambres disponibles...
        </h3>
        <p className="text-sm text-gray-500 mt-2">
          Chargement en cours, veuillez patienter...
        </p>
      </div>
    );
  }

  return (
    <div
      className="py-8 px-4"
      style={{ backgroundColor: "var(--hotel-background-color)" }}
    >
      <div className="max-w-4xl mx-auto">
        {/* Booking Summary */}
        <div className="hotel-card p-4 mb-8">
          <h3
            className="text-lg font-semibold mb-2"
            style={{ color: "var(--hotel-secondary-color)" }}
          >
            Récapitulatif de votre réservation
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <span className="font-bold">Arrivée:</span>{" "}
              {formatDate(formData.checkIn)}
            </div>
            <div>
              <span className="font-bold">Départ:</span>{" "}
              {formatDate(formData.checkOut)}
            </div>
            <div>
              <span className="font-bold">Durée:</span> {nights} nuits
            </div>
          </div>
        </div>

        <h2
          className="text-2xl font-semibold mb-6"
          style={{ color: "var(--hotel-secondary-color)" }}
        >
          {content.aboutTitle || "Sélectionnez vos chambres"}
        </h2>

        {/* Room Cards */}
        <div className="space-y-6 mb-8">
          {/* {content.aboutContent && (
            <div className="hotel-card p-6 mb-6">
              <div
                className="prose prose-lg max-w-none"
                dangerouslySetInnerHTML={{
                  __html: content.aboutContent
                    .replace(/\n\n/g, "</p><p>")
                    .replace(/\n/g, "<br>"),
                }}
              />
            </div>
          )} */}
          {rooms.map((room) => (
            <div key={room.id} className="hotel-card overflow-hidden">
              <div className="grid grid-cols-1 md:grid-cols-3">
                {/* Room Image */}
                <div className="h-56 bg-gray-200">
                  <img
                    src={room.images?.[0] ? getFullImageUrl(room.images[0]) : "https://images.pexels.com/photos/271624/pexels-photo-271624.jpeg"}
                    alt={room.name}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Room Info */}
                <div className="p-4 md:col-span-2">
                  <div className="flex flex-col md:flex-row justify-between">
                    <div>
                      <h3
                        className="text-xl font-semibold mb-2"
                        style={{ color: "var(--hotel-secondary-color)" }}
                      >
                        {room.name}
                      </h3>
                      <p className="text-gray-600 mb-3">{room.description}</p>

                      {/* Room Details */}
                      <div className="grid grid-cols-2 gap-2 mb-4">
                        <div className="flex items-center text-sm text-gray-600">
                          <User className="w-4 h-4 mr-1" />
                          <span>Max {room.max_occupancy} personnes</span>
                        </div>
                      </div>
                    </div>

                    {/* Price and Controls */}
                    <div className="mt-4 md:mt-0 flex flex-col items-end">
                      <div className="hotel-price hotel-price-large mb-2">
                        {room.base_rate} €{" "}
                        <span className="hotel-price-small">/ nuit</span>
                      </div>

                      {/* Room Counter */}
                      <div className="flex items-center space-x-3 mt-4">
                        <button
                          onClick={() => handleRoomCount(room.id, false)}
                          className="hotel-counter-btn"
                          aria-label="Diminuer"
                        >
                          <Minus className="w-4 h-4" />
                        </button>

                        <span className="w-8 text-center font-medium">
                          {roomCounts[room.id] || 0}
                        </span>

                        <button
                          onClick={() => handleRoomCount(room.id, true)}
                          className="hotel-counter-btn"
                          aria-label="Augmenter"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Summary and Navigation */}
        <div className="hotel-card p-6">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-4 md:mb-0">
              <p className="text-lg hotel-loading">
                Total sélectionné:{" "}
                <span className="font-medium">{getTotalRooms()} chambres</span>
              </p>
              <p className="text-2xl font-bold hotel-price">
                {totalPrice} €{" "}
                <span className="text-sm font-normal">pour {nights} nuits</span>
              </p>
              
              {/* Per night breakdown */}
              {getTotalRooms() > 0 && (
                <div className="text-sm text-gray-600 mt-2">
                  <div className="space-y-1">
                    {Object.keys(roomCounts)
                      .filter(roomId => roomCounts[roomId] > 0)
                      .map(roomId => {
                        const room = rooms.find(r => r.id === roomId);
                        const count = roomCounts[roomId];
                        const pricePerNight = (room?.base_rate || 0) * count;
                        const totalRoomPrice = pricePerNight * nights;
                        
                        return (
                          <div key={roomId} className="text-xs">
                            <span className="font-medium">{count}x {room?.name}</span>
                            <span className="ml-2 text-gray-500">
                              {pricePerNight} € par nuit × {nights} nuit{nights > 1 ? 's' : ''} = {totalRoomPrice} €
                            </span>
                          </div>
                        );
                      })
                    }
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={handleContinue}
              disabled={getTotalRooms() === 0}
              className="hotel-btn-primary flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Continuer
              <ArrowRight className="ml-2 w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoomsStep;
